import type { LiveUpdate } from './api';

type Numeric = string | number | null | undefined;
type Outcome = { tokenId?: string | null; price?: string | number | null } | null | undefined;

export function toNumber(value: Numeric): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function pct(price: Numeric): number | null {
  const n = toNumber(price);
  return n == null ? null : Math.round(n * 100);
}

function trimShort(n: number): string {
  return Math.abs(n) < 10 ? n.toFixed(1).replace(/\.0$/, '') : `${Math.round(n)}`;
}

export function formatMoneyShort(value: Numeric): string {
  const n = toNumber(value);
  if (n == null) return '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${trimShort(n / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${trimShort(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${trimShort(n / 1_000)}k`;
  return `${Math.round(n)}`;
}

function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatTime(iso: string | null | undefined): string {
  const d = parseDate(iso);
  return d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
}

export function formatDate(iso: string | null | undefined): string {
  const d = parseDate(iso);
  return d ? d.toLocaleDateString() : '';
}

export function formatDateTime(iso: string | null | undefined): string {
  const d = parseDate(iso);
  return d ? d.toLocaleString() : '';
}

export function abbreviate(name: string | null | undefined): string {
  if (!name) return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words.slice(0, 3).map((w) => w.charAt(0)).join('').toUpperCase();
}

export type LiveHelpers = {
  livePayload: (tokenId: string | null | undefined) => Record<string, unknown> | null;
  liveField: (tokenId: string | null | undefined, field: string) => unknown;
  priceOf: (outcome: Outcome) => number | null;
  pctOf: (outcome: Outcome) => number | null;
  sellPriceOf: (outcome: Outcome) => number | null;
  sellPctOf: (outcome: Outcome) => number | null;
};

function bestFromPayload(
  payload: Record<string, unknown> | null,
  side: 'ask' | 'bid',
): string | undefined {
  if (!payload) return undefined;
  const flat = side === 'ask' ? payload.bestAsk : payload.bestBid;
  if (typeof flat === 'string') return flat;
  const levels = side === 'ask' ? payload.asks : payload.bids;
  if (!Array.isArray(levels) || levels.length === 0) return undefined;
  let best: string | undefined;
  for (const lvl of levels) {
    const p = (lvl as { price?: unknown })?.price;
    if (typeof p !== 'string') continue;
    if (best === undefined) {
      best = p;
      continue;
    }
    const pn = Number(p);
    const bn = Number(best);
    if (side === 'ask' ? pn < bn : pn > bn) best = p;
  }
  return best;
}

export function makeLiveHelpers(liveByTokenId: Record<string, LiveUpdate>): LiveHelpers {
  const livePayload = (tokenId: string | null | undefined) => {
    if (!tokenId) return null;
    return (liveByTokenId[tokenId]?.payload as Record<string, unknown> | undefined) ?? null;
  };
  const liveField = (tokenId: string | null | undefined, field: string) => {
    return livePayload(tokenId)?.[field];
  };
  const priceOf = (outcome: Outcome) => {
    if (!outcome) return null;
    const payload = livePayload(outcome.tokenId);
    const ask = payload ? bestFromPayload(payload, 'ask') : undefined;
    if (ask !== undefined) return toNumber(ask);
    return toNumber(outcome.price);
  };
  const pctOf = (outcome: Outcome) => {
    const n = priceOf(outcome);
    return n == null ? null : Math.round(n * 100);
  };
  const sellPriceOf = (outcome: Outcome) => {
    if (!outcome) return null;
    const payload = livePayload(outcome.tokenId);
    const bid = payload ? bestFromPayload(payload, 'bid') : undefined;
    if (bid !== undefined) return toNumber(bid);
    return toNumber(outcome.price);
  };
  const sellPctOf = (outcome: Outcome) => {
    const n = sellPriceOf(outcome);
    return n == null ? null : Math.round(n * 100);
  };
  return { livePayload, liveField, priceOf, pctOf, sellPriceOf, sellPctOf };
}

export type Helpers = LiveHelpers & {
  toNumber: typeof toNumber;
  pct: typeof pct;
  formatMoneyShort: typeof formatMoneyShort;
  formatTime: typeof formatTime;
  formatDate: typeof formatDate;
  formatDateTime: typeof formatDateTime;
  abbreviate: typeof abbreviate;
};

export function makeHelpers(liveByTokenId: Record<string, LiveUpdate>): Helpers {
  return {
    ...makeLiveHelpers(liveByTokenId),
    toNumber,
    pct,
    formatMoneyShort,
    formatTime,
    formatDate,
    formatDateTime,
    abbreviate,
  };
}
